import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, MagnifyingGlassIcon, PlusIcon, ArrowUturnLeftIcon, ArchiveBoxIcon, UserPlusIcon, CheckCircleIcon, ChevronDownIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useDevices, useAddDevice, useUpdateDevice, useSyncDevices } from '../hooks/useDevices';

// Device / asset register — the ISO 27001 laptop-provisioning evidence and the
// live estate. Same two-view shape as the SIM manager (People coverage +
// Inventory), plus a per-device provisioning checklist taken straight from
// NF-WI-045 "How to Configure Endpoint Devices". Logistics own the estate; IT
// tick the checklist at build; several items will auto-fill later from the
// Wazuh / Atera / Miradore device sync.

const STATUS_STYLES = {
  in_service: 'bg-emerald-100 text-emerald-800 ring-emerald-600/30',
  spare: 'bg-sky-100 text-sky-800 ring-sky-600/30',
  broken: 'bg-rose-100 text-rose-800 ring-rose-600/30',
  repairing: 'bg-orange-100 text-orange-800 ring-orange-600/30',
  returned: 'bg-amber-100 text-amber-800 ring-amber-600/30',
  retired: 'bg-gray-100 text-gray-600 ring-gray-500/30',
};
const STATUS_LABELS = { in_service: 'In service', spare: 'Spare', broken: 'Broken', repairing: 'Repairing', returned: 'Returned', retired: 'Retired' };

// NF-WI-045 build steps, AnyDesk first (it goes on first — it's how the rest is
// installed remotely). The auto-flagged ones fill from the device sync later.
const CHECKLIST = [
  { key: 'anydesk', label: 'AnyDesk' },
  { key: 'business_apps', label: 'Microsoft Apps' },
  { key: 'device_named', label: 'Device Rename' },
  { key: 'user_account_set', label: 'Entra Account' },
  { key: 'bitlocker', label: 'BitLocker', auto: true },
  { key: 'atera_agent', label: 'Atera', auto: true },
  { key: 'wazuh_agent', label: 'Wazuh', auto: true },
  { key: 'activtrak', label: 'ActivTrak', auto: true },
];
const STATUS_OPTIONS = [['in_service', 'In service'], ['spare', 'Spare'], ['broken', 'Broken'], ['repairing', 'Repairing'], ['returned', 'Returned'], ['retired', 'Retired']];
const OWNERSHIP_OPTIONS = [['nfuk', 'NFUK'], ['veldora', 'Veldora'], ['byod', 'BYOD']];
const OWNERSHIP_STYLES = { nfuk: 'bg-sky-100 text-sky-700', veldora: 'bg-indigo-100 text-indigo-700', byod: 'bg-amber-100 text-amber-800' };
const OWNERSHIP_LABELS = { nfuk: 'NFUK', veldora: 'Veldora', byod: 'BYOD' };
function OwnershipBadge({ ownership }) {
  if (!ownership) return null;
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${OWNERSHIP_STYLES[ownership] || 'bg-gray-100 text-gray-600'}`}>{OWNERSHIP_LABELS[ownership] || ownership}</span>;
}

// Retirement / disposal reasons (ISO A.8.10 secure-disposal evidence).
const RETIRE_REASONS = [['', '— reason —'], ['sold', 'Sold'], ['broken', 'Broken (beyond repair)'], ['lost', 'Lost'], ['stolen', 'Stolen'], ['donated', 'Donated'], ['recycled', 'Recycled'], ['end_of_life', 'End of life'], ['other', 'Other']];

// Inventory is grouped into these sections (each its own table). Every status
// has a home so nothing is hidden.
const SECTIONS = [
  { key: 'in_service', label: 'In Service', match: (d) => d.status === 'in_service', variant: 'full' },
  { key: 'spare', label: 'Spare / Returned', match: (d) => d.status === 'spare' || d.status === 'returned', variant: 'spare' },
  { key: 'repair', label: 'In for Repair', match: (d) => d.status === 'broken' || d.status === 'repairing', variant: 'repair' },
  { key: 'retired', label: 'Retired / Disposed', match: (d) => d.status === 'retired', variant: 'retired' },
];
// Per-variant columns. Stock that isn't in a person's hands only needs serial +
// device — the owner/agents/setup detail lights up once it's assigned (full).
// Repair adds a Notes column for what's wrong; retired keeps the data-wiped
// disposal attestation. All the rest is still editable in the expand panel.
const VARIANT_HEADERS = {
  full: ['Serial', 'Device', 'Owner', 'Held by', 'Status', 'Atera', 'Wazuh', 'BitLocker', 'Setup', ''],
  spare: ['Serial', 'Device', ''],
  repair: ['Serial', 'Device', 'Notes', ''],
  retired: ['Serial', 'Device', 'Data wiped', ''],
};
const VARIANT_COLSPAN = { full: 10, spare: 3, repair: 4, retired: 4 };
const PAGE_SIZE = 15;
// Sort accessors keyed by header label — a header with an accessor is clickable.
const SORT_ACCESSORS = {
  Serial: (d) => (d.serial_number || '').toLowerCase(),
  Device: (d) => (d.make_model || d.hostname || '').toLowerCase(),
  Owner: (d) => d.ownership || '',
  'Held by': (d) => (d.assigned_name || '').toLowerCase(),
  Status: (d) => d.status || '',
  Atera: (d) => (d.atera_agent ? 1 : 0),
  Wazuh: (d) => (d.wazuh_agent ? 1 : 0),
  BitLocker: (d) => (d.bitlocker ? 1 : 0),
  Setup: (d) => CHECKLIST.filter((c) => d[c.key]).length,
  Reason: (d) => d.retire_reason || '',
  'Data wiped': (d) => (d.data_wiped ? 1 : 0),
};

// UK format everywhere (DD/MM/YYYY) — store ISO, format on display.
function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Compact ✓ / — for the agent + encryption columns.
function Tick({ on }) {
  return on
    ? <CheckCircleIcon className="mx-auto h-4 w-4 text-emerald-600" />
    : <span className="mx-auto block h-4 w-4 text-center leading-4 text-gray-300">—</span>;
}

function StatusChip({ status }) {
  return (
    <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_STYLES[status] || STATUS_STYLES.retired}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function ChecklistBar({ device }) {
  const done = CHECKLIST.filter((c) => device[c.key]).length;
  const pct = Math.round((done / CHECKLIST.length) * 100);
  const complete = done === CHECKLIST.length;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200">
        <div className={`h-full ${complete ? 'bg-emerald-500' : 'bg-brand'}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs tabular-nums ${complete ? 'text-emerald-700' : 'text-gray-500'}`}>{done}/{CHECKLIST.length}</span>
    </div>
  );
}

// Typeahead picker — same behaviour as the SIM manager's, so assigning a device
// feels like assigning a SIM.
function Picker({ label, options, onPick, onCancel, placeholder }) {
  const [q, setQ] = useState('');
  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return options.slice(0, 8);
    return options.filter((o) => o.label.toLowerCase().includes(t)).slice(0, 12);
  }, [q, options]);
  return (
    <div className="rounded-md border border-gray-200 bg-white p-2 shadow-sm">
      <p className="mb-1 text-xs font-medium text-gray-500">{label}</p>
      <input
        autoFocus value={q} onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && onCancel()}
        placeholder={placeholder}
        className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
      />
      <ul className="mt-1 max-h-48 overflow-y-auto">
        {matches.map((o) => (
          <li key={o.value}>
            <button type="button" onClick={() => onPick(o.value)} className="w-full px-2 py-1 text-left text-sm hover:bg-brand-light">
              {o.label}
            </button>
          </li>
        ))}
        {matches.length === 0 ? <li className="px-2 py-2 text-xs text-gray-400">No match.</li> : null}
      </ul>
      <button type="button" onClick={onCancel} className="mt-1 text-xs text-gray-500 underline">Cancel</button>
    </div>
  );
}

const inputCls = 'rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

function AddDeviceForm({ people, onClose }) {
  const [form, setForm] = useState({ serial_number: '', hostname: '', make_model: '', device_type: 'laptop', ownership: 'nfuk', assigned_entra_user_id: '', provisioned_by: '', provisioned_at: '', notes: '' });
  const [checks, setChecks] = useState({});
  const [error, setError] = useState(null);
  const add = useAddDevice();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => setChecks((c) => ({ ...c, [k]: !c[k] }));
  const doneCount = CHECKLIST.filter((c) => checks[c.key]).length;

  const submit = async () => {
    setError(null);
    try {
      await add.mutateAsync({ ...form, ...checks });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add that device.');
    }
  };

  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <h2 className="mb-3 font-medium text-gray-900">Add a device to the register</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">
          Serial number *
          <input value={form.serial_number} onChange={set('serial_number')} placeholder="Device serial" className={inputCls} />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">
          Hostname / device name
          <input value={form.hostname} onChange={set('hostname')} placeholder="NF-LT-…" className={inputCls} />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">
          Make &amp; model
          <input value={form.make_model} onChange={set('make_model')} placeholder="Dell Latitude 5540" className={inputCls} />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">
          Device type
          <select value={form.device_type} onChange={set('device_type')} className={inputCls}>
            <option value="laptop">Laptop</option>
            <option value="desktop">Desktop</option>
          </select>
        </label>
        <div className="flex flex-col gap-1 text-xs font-medium text-gray-500">
          Ownership
          <div className="flex flex-col gap-1 pt-1">
            {OWNERSHIP_OPTIONS.map(([v, l]) => (
              <label key={v} className="flex items-center gap-2 text-sm font-normal text-gray-700">
                <input type="radio" name="add-ownership" value={v} checked={form.ownership === v} onChange={set('ownership')}
                  className="h-4 w-4 border-gray-300 text-brand focus:ring-brand" />
                {l}
              </label>
            ))}
          </div>
        </div>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">
          Assign to
          <select value={form.assigned_entra_user_id} onChange={set('assigned_entra_user_id')} className={inputCls}>
            <option value="">— Spare / stock —</option>
            {people.map((p) => <option key={p.entra_user_id} value={p.entra_user_id}>{p.display_name}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">
          Provisioned by
          <select value={form.provisioned_by} onChange={set('provisioned_by')} className={inputCls}>
            <option value="">— Me —</option>
            {people.map((p) => <option key={p.entra_user_id} value={p.display_name}>{p.display_name}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">
          Provisioned date
          <input type="date" value={form.provisioned_at} onChange={set('provisioned_at')} className={inputCls} />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500 sm:col-span-2 lg:col-span-1">
          Notes
          <input value={form.notes} onChange={set('notes')} placeholder="Anything non-standard" className={inputCls} />
        </label>
      </div>

      <div className="mt-4">
        <p className="mb-2 text-xs font-medium text-gray-500">Provisioning checklist (NF-WI-045) — {doneCount}/{CHECKLIST.length}</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {CHECKLIST.map((c) => (
            <button key={c.key} type="button" onClick={() => toggle(c.key)}
              className={`flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm ${checks[c.key] ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'}`}>
              <CheckCircleIcon className={`h-4 w-4 shrink-0 ${checks[c.key] ? 'text-emerald-600' : 'text-gray-300'}`} />
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex gap-3">
        <button type="button" onClick={submit} disabled={add.isPending || !form.serial_number.trim()}
          className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-50">
          {add.isPending ? 'Adding…' : 'Add device'}
        </button>
        <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-white">Cancel</button>
      </div>
      {error ? <p className="mt-2 text-sm text-rose-700">{error}</p> : null}
    </div>
  );
}

// Expanded per-device detail: full checklist (each toggle saves on click) plus
// the editable identity / provisioning fields.
function DeviceDetail({ device }) {
  const update = useUpdateDevice();
  const [fields, setFields] = useState({
    serial_number: device.serial_number || '', hostname: device.hostname || '', make_model: device.make_model || '',
    device_type: device.device_type || 'laptop', status: device.status || 'in_service', ownership: device.ownership || 'nfuk',
    retire_reason: device.retire_reason || '', data_wiped: device.data_wiped ? 1 : 0,
    provisioned_by: device.provisioned_by || '', provisioned_at: device.provisioned_at ? device.provisioned_at.slice(0, 10) : '', notes: device.notes || '',
  });
  const [saved, setSaved] = useState(false);
  const set = (k) => (e) => { setFields({ ...fields, [k]: e.target.value }); setSaved(false); };
  const toggle = (k) => update.mutate({ id: device.id, [k]: !device[k] });
  const saveFields = async () => { await update.mutateAsync({ id: device.id, ...fields }); setSaved(true); };

  return (
    <div className="bg-gray-50 px-4 py-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {CHECKLIST.map((c) => (
          <button key={c.key} type="button" onClick={() => toggle(c.key)}
            className={`flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm ${device[c.key] ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'}`}>
            <CheckCircleIcon className={`h-4 w-4 shrink-0 ${device[c.key] ? 'text-emerald-600' : 'text-gray-300'}`} />
            {c.label}{c.auto ? <span className="ml-auto text-[10px] uppercase tracking-wide text-gray-400">auto</span> : null}
          </button>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">Serial<input value={fields.serial_number} onChange={set('serial_number')} className={inputCls} /></label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">Hostname<input value={fields.hostname} onChange={set('hostname')} className={inputCls} /></label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">Make &amp; model<input value={fields.make_model} onChange={set('make_model')} className={inputCls} /></label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">Type
          <select value={fields.device_type} onChange={set('device_type')} className={inputCls}><option value="laptop">Laptop</option><option value="desktop">Desktop</option></select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">Status
          <select value={fields.status} onChange={set('status')} className={inputCls}>{STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </label>
        <div className="flex flex-col gap-1 text-xs font-medium text-gray-500">Ownership
          <div className="flex flex-col gap-1 pt-1">
            {OWNERSHIP_OPTIONS.map(([v, l]) => (
              <label key={v} className="flex items-center gap-2 text-sm font-normal text-gray-700">
                <input type="radio" name={`own-${device.id}`} value={v} checked={fields.ownership === v} onChange={set('ownership')}
                  className="h-4 w-4 border-gray-300 text-brand focus:ring-brand" />
                {l}
              </label>
            ))}
          </div>
        </div>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">Provisioned by<input value={fields.provisioned_by} onChange={set('provisioned_by')} className={inputCls} /></label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">Provisioned date<input type="date" value={fields.provisioned_at} onChange={set('provisioned_at')} className={inputCls} /></label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">Notes<input value={fields.notes} onChange={set('notes')} className={inputCls} /></label>
      </div>

      {fields.status === 'retired' ? (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-800">Retirement / disposal</p>
          <div className="flex flex-wrap items-end gap-4">
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-500">Reason
              <select value={fields.retire_reason} onChange={set('retire_reason')} className={inputCls}>
                {RETIRE_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="mb-2 flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={!!fields.data_wiped}
                onChange={(e) => { setFields({ ...fields, data_wiped: e.target.checked ? 1 : 0 }); setSaved(false); }}
                className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand" />
              Data wiped / drive sanitised
            </label>
          </div>
        </div>
      ) : null}

      <div className="mt-3 flex items-center gap-3">
        <button type="button" onClick={saveFields} disabled={update.isPending} className="rounded-md bg-brand px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-50">Save details</button>
        {saved ? <span className="text-xs text-emerald-700">Saved.</span> : null}
      </div>
    </div>
  );
}

function DeviceRegister() {
  const { data, isLoading, error } = useDevices();
  const update = useUpdateDevice();
  const sync = useSyncDevices();
  const [view, setView] = useState('inventory');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [adding, setAdding] = useState(false);
  const [picking, setPicking] = useState(null); // { kind:'device'|'person', id }
  const [expanded, setExpanded] = useState(null);
  const [message, setMessage] = useState(null);
  const [pages, setPages] = useState({}); // per-section page index
  const [sort, setSort] = useState({}); // per-section { col, dir }
  const toggleSort = (secKey, col) => setSort((prev) => {
    const cur = prev[secKey];
    const dir = cur && cur.col === col && cur.dir === 'asc' ? 'desc' : 'asc';
    return { ...prev, [secKey]: { col, dir } };
  });

  const devices = useMemo(() => data?.devices || [], [data]);
  const people = useMemo(() => data?.people || [], [data]);
  const summary = data?.summary;

  const spareOptions = useMemo(
    () => devices.filter((d) => d.status === 'spare' || d.status === 'returned')
      .map((d) => ({ value: d.id, label: `${d.serial_number || 'no serial'} — ${[d.make_model, d.device_type].filter(Boolean).join(' ')}` })),
    [devices]
  );
  const personOptions = useMemo(
    () => people.map((p) => ({ value: p.entra_user_id, label: `${p.display_name}${p.device_count ? ` — has ${p.device_count}` : ''}` })),
    [people]
  );

  const runSync = async () => {
    setMessage(null);
    try {
      const r = await sync.mutateAsync();
      const a = r.atera || {}; const w = r.wazuh || {};
      const parts = [
        a.error ? `Atera failed: ${a.error}` : `Atera: +${a.created} new, ${a.updated} updated`,
        w.error ? `Wazuh failed: ${w.error}` : `Wazuh: +${w.created} new, ${w.updated} matched`,
      ];
      setMessage(`Sync complete — ${parts.join(' · ')}.`);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Sync failed.');
    }
  };

  const act = async (id, body, note) => {
    setMessage(null);
    try {
      await update.mutateAsync({ id, ...body });
      setPicking(null);
      if (note) setMessage(note);
    } catch (err) {
      setMessage(err.response?.data?.error || 'That did not work.');
    }
  };

  if (isLoading) return <div className="p-8"><p className="text-sm text-gray-400">Loading devices…</p></div>;
  if (error) {
    return (
      <div className="p-8">
        <p className="text-sm text-rose-700">
          {error.response?.status === 403 ? 'The device register is restricted to Logistics.' : 'Could not load the device register.'}
        </p>
      </div>
    );
  }

  // Inventory is grouped into status sections, so filter by search only here.
  const searchedDevices = devices.filter((d) => {
    const t = search.trim().toLowerCase();
    if (!t) return true;
    return [d.serial_number, d.hostname, d.assigned_name, d.make_model, d.device_type].some((v) => String(v ?? '').toLowerCase().includes(t));
  });

  // One device row (+ its expandable detail). Retired rows swap the status/agent
  // columns for the disposal reason + data-wiped columns.
  const deviceRow = (d, variant = 'full') => (
    <Fragment key={d.id}>
      <tr className="border-t border-gray-100 hover:bg-gray-50">
        <td className="px-3 py-2 font-medium tabular-nums text-gray-900">
          <button type="button" onClick={() => setExpanded(expanded === d.id ? null : d.id)} className="inline-flex items-center gap-1 hover:text-brand">
            <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${expanded === d.id ? 'rotate-180' : ''}`} />
            {d.serial_number || <span className="text-gray-400">no serial</span>}
          </button>
        </td>
        <td className="px-3 py-2 text-gray-600">
          <div>{d.make_model || '—'}</div>
          <div className="text-xs text-gray-400">{d.hostname || d.device_type}</div>
        </td>
        {variant === 'full' ? (
          <>
            <td className="px-3 py-2"><OwnershipBadge ownership={d.ownership} /></td>
            <td className="px-3 py-2 text-gray-600">{d.assigned_name || '—'}</td>
            <td className="px-3 py-2"><StatusChip status={d.status} /></td>
            <td className="px-3 py-2 text-center"><Tick on={d.atera_agent} /></td>
            <td className="px-3 py-2 text-center"><Tick on={d.wazuh_agent} /></td>
            <td className="px-3 py-2 text-center"><Tick on={d.bitlocker} /></td>
            <td className="px-3 py-2"><ChecklistBar device={d} /></td>
          </>
        ) : null}
        {variant === 'repair' ? (
          <td className="px-3 py-2 text-xs text-gray-600"><span className="block max-w-md break-words">{d.notes || <span className="text-gray-400">— add what's wrong in the expand panel —</span>}</span></td>
        ) : null}
        {variant === 'retired' ? (
          <td className="px-3 py-2 text-center">{d.data_wiped ? <CheckCircleIcon className="mx-auto h-4 w-4 text-emerald-600" /> : <span className="text-xs font-medium text-rose-600">no</span>}</td>
        ) : null}
        <td className="px-3 py-2 text-right">
          {picking?.kind === 'person' && picking.id === d.id ? (
            <Picker label={`Assign ${d.serial_number || 'device'}`} options={personOptions}
              placeholder="Search people…" onCancel={() => setPicking(null)}
              onPick={(entraId) => act(d.id, { action: 'assign', assigned_entra_user_id: entraId }, 'Device assigned.')} />
          ) : (
            <div className="flex justify-end gap-1">
              {d.status !== 'retired' ? (
                <button type="button" onClick={() => setPicking({ kind: 'person', id: d.id })}
                  className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                  {d.status === 'in_service' ? 'Reassign' : 'Assign'}
                </button>
              ) : null}
              {d.status === 'in_service' ? (
                <button type="button" onClick={() => act(d.id, { action: 'return' }, `${d.serial_number || 'Device'} returned to stock.`)}
                  className="flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                  <ArrowUturnLeftIcon className="h-3.5 w-3.5" /> Return
                </button>
              ) : null}
              {d.status !== 'retired' ? (
                <button type="button" onClick={() => act(d.id, { action: 'retire' }, `${d.serial_number || 'Device'} retired.`)}
                  className="flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                  <ArchiveBoxIcon className="h-3.5 w-3.5" /> Retire
                </button>
              ) : null}
            </div>
          )}
        </td>
      </tr>
      {expanded === d.id ? (
        <tr>
          <td colSpan={VARIANT_COLSPAN[variant]} className="border-t border-gray-100 p-0"><DeviceDetail device={d} /></td>
        </tr>
      ) : null}
    </Fragment>
  );

  const filteredPeople = people.filter((p) => {
    if (statusFilter === 'spare' && p.device_count) return false; // "without a device"
    const t = search.trim().toLowerCase();
    if (!t) return true;
    return [p.display_name, p.email].some((v) => String(v ?? '').toLowerCase().includes(t));
  });

  // Enrolment log: every device as a provisioning event, newest first. A
  // read-only history over the same data — provisioned_at is the enrolment
  // date (falls back to when the row was created), provisioned_by is who did it.
  const enrolmentLog = devices
    .filter((d) => {
      const t = search.trim().toLowerCase();
      if (!t) return true;
      return [d.serial_number, d.hostname, d.assigned_name, d.make_model, d.provisioned_by]
        .some((v) => String(v ?? '').toLowerCase().includes(t));
    })
    .slice()
    .sort((a, b) => new Date(b.provisioned_at || b.created_at || 0) - new Date(a.provisioned_at || a.created_at || 0));

  return (
    <div className="p-4 sm:p-8">
      <Link to="/?dept=logistics" className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" /> Back to Logistics
      </Link>
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gray-900">Device register</h1>
        <p className="mt-1 text-sm text-gray-500">
          {summary.total} devices — {summary.inService} in service, {summary.spare} spare
          {summary.returned ? `, ${summary.returned} returned` : ''}{summary.retired ? `, ${summary.retired} retired` : ''}.
          {' '}{summary.peopleWithout} of {summary.peopleTotal} staff have no device on record.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex rounded-md border border-gray-300">
          {[['inventory', 'Inventory'], ['people', 'People'], ['log', 'Enrolment log']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setView(k)}
              className={`px-4 py-2 text-sm font-medium ${view === k ? 'bg-brand text-white' : 'text-gray-700 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="relative min-w-[14rem] flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search serial, name, model…"
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
        </div>

        {view === 'people' ? (
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand">
            <option value="all">Everyone ({summary.peopleTotal})</option>
            <option value="spare">Without a device ({summary.peopleWithout})</option>
          </select>
        ) : null}

        <button type="button" onClick={runSync} disabled={sync.isPending}
          className="flex items-center gap-1.5 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          <ArrowPathIcon className={`h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`} /> {sync.isPending ? 'Syncing…' : 'Sync now'}
        </button>
        <button type="button" onClick={() => setAdding((v) => !v)}
          className="flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark">
          <PlusIcon className="h-4 w-4" /> Add device
        </button>
      </div>

      {adding ? <AddDeviceForm people={people} onClose={() => setAdding(false)} /> : null}
      {message ? <p className="mb-3 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700">{message}</p> : null}

      {view === 'inventory' ? (
        <div className="space-y-6">
          {SECTIONS.map((sec) => {
            const matched = searchedDevices.filter(sec.match);
            if (!matched.length) return null;
            const s = sort[sec.key];
            let rows = matched;
            if (s && SORT_ACCESSORS[s.col]) {
              const acc = SORT_ACCESSORS[s.col];
              rows = matched.slice().sort((a, b) => {
                const va = acc(a); const vb = acc(b);
                if (va < vb) return s.dir === 'asc' ? -1 : 1;
                if (va > vb) return s.dir === 'asc' ? 1 : -1;
                return 0;
              });
            }
            const pageCount = Math.ceil(rows.length / PAGE_SIZE);
            const page = Math.min(pages[sec.key] || 0, pageCount - 1);
            const shown = rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
            return (
              <div key={sec.key}>
                <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
                  {sec.label}
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">{matched.length}</span>
                </h2>
                <div className="overflow-auto rounded-lg border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead><tr className="bg-gray-50">
                      {VARIANT_HEADERS[sec.variant].map((h, i) => {
                        const sortable = Boolean(SORT_ACCESSORS[h]);
                        const active = s && s.col === h;
                        return (
                          <th key={i} className="whitespace-nowrap bg-gray-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                            {sortable ? (
                              <button type="button" onClick={() => toggleSort(sec.key, h)} className="inline-flex items-center gap-1 uppercase hover:text-gray-800">
                                {h}<span className={active ? 'text-brand' : 'text-gray-300'}>{active ? (s.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
                              </button>
                            ) : h}
                          </th>
                        );
                      })}
                    </tr></thead>
                    <tbody>{shown.map((d) => deviceRow(d, sec.variant))}</tbody>
                  </table>
                </div>
                {pageCount > 1 ? (
                  <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                    <span>Showing {page * PAGE_SIZE + 1}–{Math.min(rows.length, (page + 1) * PAGE_SIZE)} of {rows.length}</span>
                    <div className="flex gap-1">
                      <button type="button" disabled={page === 0} onClick={() => setPages((p) => ({ ...p, [sec.key]: page - 1 }))}
                        className="rounded-md border border-gray-300 px-2.5 py-1 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">Prev</button>
                      <span className="px-1 py-1 tabular-nums">{page + 1} / {pageCount}</span>
                      <button type="button" disabled={page >= pageCount - 1} onClick={() => setPages((p) => ({ ...p, [sec.key]: page + 1 }))}
                        className="rounded-md border border-gray-300 px-2.5 py-1 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">Next</button>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
          {searchedDevices.length === 0 ? <p className="text-sm text-gray-400">No devices match your search.</p> : null}
        </div>
      ) : (
      <div className="max-h-[70vh] overflow-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              {(view === 'people'
                ? ['Person', 'Email', 'Devices', '']
                : ['Enrolled', 'Serial', 'Device', 'Assigned to', 'Enrolled by', 'Setup']
              ).map((h, i) => (
                <th key={i} className="sticky top-0 z-10 whitespace-nowrap bg-gray-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view === 'people' && filteredPeople.map((p) => (
              <tr key={p.entra_user_id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 font-medium text-gray-900">{p.display_name}</td>
                <td className="px-3 py-2 text-gray-600">{p.email || '—'}</td>
                <td className="px-3 py-2 tabular-nums">
                  {p.device_count ? `${p.device_count} device${p.device_count > 1 ? 's' : ''}` : <span className="text-gray-400">none</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  {picking?.kind === 'device' && picking.id === p.entra_user_id ? (
                    <Picker label={`Give ${p.display_name.split(' ')[0]} a device`} options={spareOptions}
                      placeholder="Search spare devices…" onCancel={() => setPicking(null)}
                      onPick={(deviceId) => act(deviceId, { action: 'assign', assigned_entra_user_id: p.entra_user_id }, `Assigned to ${p.display_name}.`)} />
                  ) : (
                    <button type="button" onClick={() => setPicking({ kind: 'device', id: p.entra_user_id })}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                      <UserPlusIcon className="h-3.5 w-3.5" /> Assign a device
                    </button>
                  )}
                </td>
              </tr>
            ))}

            {view === 'log' && enrolmentLog.map((d) => (
              <tr key={d.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-600">{fmtDate(d.provisioned_at || d.created_at)}</td>
                <td className="px-3 py-2 font-medium tabular-nums text-gray-900">{d.serial_number || <span className="text-gray-400">no serial</span>}</td>
                <td className="px-3 py-2 text-gray-600">{[d.make_model, d.device_type].filter(Boolean).join(' · ') || '—'}</td>
                <td className="px-3 py-2 text-gray-600">{d.assigned_name || <span className="text-gray-400">Spare stock</span>}</td>
                <td className="px-3 py-2 text-gray-600">{d.provisioned_by || '—'}</td>
                <td className="px-3 py-2"><ChecklistBar device={d} /></td>
              </tr>
            ))}
            {view === 'log' && enrolmentLog.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-400">No devices enrolled yet.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
      )}

      <p className="mt-3 text-xs text-gray-400">
        Returning or retiring keeps the device on record rather than deleting it — the provisioning history and the leaver return trail survive.
        Items marked <span className="font-medium">auto</span> will fill from the Wazuh / Atera / Miradore sync once connected.
      </p>
    </div>
  );
}

export default DeviceRegister;
